// Affiliate-payout BE error → operator-readable message mapper.
//
// The BE returns structured error envelopes of the shape:
//
//     { error: <slug>, message?: <string>, reason?: <slug> }
//
// We map known slugs to short, safe operator-facing strings. Unknown
// slugs fall through to "HTTP <status> <slug> — <message>" so the
// operator still gets the raw signal without us swallowing the BE's
// rejection. No stack traces, no body dumps. The refund-gate slug
// gets a specific copy-pasted line from the operator brief so the
// admin knows the underlying referred purchase is no longer eligible.

import { ApiHttpError, RouteNotFoundError } from "./client";

export interface MappedError {
  /** Short, safe one-liner suitable for inline rendering. */
  message: string;
  /** Slug from the BE envelope, when one was present. Used in tests
   *  and the debug surface but never required by the UI. */
  code: string | null;
  /** Raw HTTP status when the source was an ApiHttpError; null when
   *  the source was a route-missing or unexpected JS error. */
  status: number | null;
}

const REFUND_GATE_COPY =
  "Underlying referred purchase is no longer eligible because it was refunded/cancelled/failed. Do not advance this request.";

const TRUSTED_DEVICE_COPY =
  "This action requires a trusted-device session. Re-login from a registered device.";

// True session-auth failure (401). Re-login is the correct remedy here.
const SESSION_EXPIRED_COPY =
  "Session expired or not authorised. Re-login from a registered device.";

// 403 from the BE owner-gate with no recognised finer-grained reason. This
// is an authorisation-POLICY refusal (e.g. a proposal problem), NOT a
// session problem — re-login will not help, so we must not tell the operator
// to re-login.
const POLICY_FORBIDDEN_COPY =
  "Action not authorised by the BE policy (HTTP 403). This is not a session problem — re-login will not help. Check the multisign proposal (approved 2-of-2?) and that the proposalId is correct.";

// Approval-stage (proposal) reasons surfaced by the BE owner-gate.
const PROPOSAL_REQUIRED_COPY =
  "Paste an approved proposalId first — copy it from Approvals, not the withdrawalId.";
const PROPOSAL_NOT_FOUND_COPY =
  "This proposalId was not found. Copy the proposal id from Approvals — do not paste a withdrawalId.";
const PROPOSAL_NOT_APPROVED_COPY =
  "Proposal exists but is not approved 2-of-2 yet. Collect quorum at /approvals first.";
const PROPOSAL_EXPIRED_COPY =
  "Proposal has expired. Create a new payout proposal and approve it 2-of-2.";
const PROPOSAL_VETOED_COPY =
  "Proposal was vetoed. This action cannot proceed.";
const PROPOSAL_CANCELLED_COPY =
  "Proposal was cancelled. Create a new payout proposal and approve it.";
const PAYLOAD_MISMATCH_COPY =
  "Proposal does not match this withdrawal row (amount/destination/identity). Use the proposal created for THIS row.";

function extractErrorEnvelope(body: unknown): {
  code: string | null;
  reason: string | null;
  message: string | null;
} {
  if (body === null || typeof body !== "object") {
    return { code: null, reason: null, message: null };
  }
  const o = body as Record<string, unknown>;
  return {
    code: typeof o["error"] === "string" ? (o["error"] as string) : null,
    reason: typeof o["reason"] === "string" ? (o["reason"] as string) : null,
    message: typeof o["message"] === "string" ? (o["message"] as string) : null,
  };
}

/** Map a slug (case-insensitive) to a safe operator-facing message.
 *  Returns null when the slug is not recognised. */
function safeMessageForSlug(slugRaw: string): string | null {
  const slug = slugRaw.toLowerCase();
  // `payload_mismatch` arrives as `payload_mismatch:<sub-reason>` from the
  // BE route, so match it by prefix before the exact-match switch.
  if (slug.startsWith("payload_mismatch")) return PAYLOAD_MISMATCH_COPY;
  switch (slug) {
    // ── session-auth reasons (401) → re-login is the correct remedy ──
    case "missing_token":
    case "expired":
    case "session_expired":
    case "revoked":
    case "compromised":
    case "invalid_input":
      return SESSION_EXPIRED_COPY;
    // ── trusted-device reasons (401) ──
    case "device_required":
    case "device_pending":
    case "device_revoked":
    case "device_compromised":
    case "trusted_device_required":
    case "trusted_device":
    case "device_not_trusted":
      return TRUSTED_DEVICE_COPY;
    // ── approval-stage (proposal) reasons (403) ──
    case "proposal_required":
      return PROPOSAL_REQUIRED_COPY;
    case "proposal_unknown":
    case "proposal_not_found":
      return PROPOSAL_NOT_FOUND_COPY;
    case "proposal_not_approved":
    case "not_approved":
      return PROPOSAL_NOT_APPROVED_COPY;
    case "proposal_under_threshold":
    case "under_threshold":
      return PROPOSAL_NOT_APPROVED_COPY;
    case "proposal_expired":
      return PROPOSAL_EXPIRED_COPY;
    case "proposal_vetoed":
    case "vetoed":
      return PROPOSAL_VETOED_COPY;
    case "proposal_cancelled":
      return PROPOSAL_CANCELLED_COPY;
    case "payload_mismatch":
      return PAYLOAD_MISMATCH_COPY;
    case "refund_terminal_drift":
      return REFUND_GATE_COPY;
    case "insufficient_matured":
      return "Insufficient matured commission to satisfy this withdrawal.";
    case "insufficient_matured_credits":
      return "Insufficient matured credits to satisfy this redemption.";
    case "already_approved":
      return "This withdrawal has already been approved.";
    case "already_executed":
    case "already_paid":
      return "This withdrawal has already been paid.";
    case "already_applied":
      return "This redemption has already been applied.";
    case "already_rejected":
      return "This request has already been rejected.";
    case "destination_unresolved":
      return "Affiliate destination wallet could not be resolved on the BE.";
    case "db_error":
    case "audit_error":
      return "A storage error occurred on the BE. Retry once; if it persists, escalate.";
    case "rate_limited":
      return "Too many requests — slow down and retry.";
    default:
      return null;
  }
}

export function mapAffiliateError(e: unknown): MappedError {
  if (e instanceof RouteNotFoundError) {
    return {
      message: `BE route not registered: ${e.path}. Check the gao-id-worker deploy.`,
      code: "route_not_found",
      status: 404,
    };
  }
  if (e instanceof ApiHttpError) {
    const env = extractErrorEnvelope(e.body);
    // Prefer the finer-grained `reason` slug over the broad `error` code.
    // The BE owner-gate now surfaces a `reason` on every fail-closed
    // envelope (session vs trusted-device vs approval/proposal), and uses
    // `reason` for business codes too (e.g. REFUND_TERMINAL_DRIFT inside a
    // generic `conflict`). Matching `reason` FIRST is what stops a proposal
    // problem (403) from rendering as the session/re-login copy.
    const candidates = [env.reason, env.code].filter(
      (s): s is string => typeof s === "string" && s !== "",
    );
    for (const c of candidates) {
      const mapped = safeMessageForSlug(c);
      if (mapped !== null) {
        return { message: mapped, code: c, status: e.status };
      }
    }
    // No recognised slug. Fall back by HTTP status so the operator gets an
    // accurate remedy — crucially, a 403 is an authorisation-POLICY refusal
    // (proposal problem), NOT a session problem, so we never tell the
    // operator to re-login on a 403. A 401 IS a session/auth failure.
    if (e.status === 401) {
      return { message: SESSION_EXPIRED_COPY, code: env.code ?? env.reason, status: 401 };
    }
    if (e.status === 403) {
      return { message: POLICY_FORBIDDEN_COPY, code: env.code ?? env.reason, status: 403 };
    }
    // Other statuses — preserve the HTTP status and any short BE message so
    // the operator has SOMETHING to escalate.
    const trailing =
      env.message !== null && env.message.length <= 200
        ? ` — ${env.message}`
        : "";
    const codeTag = env.code !== null ? ` [${env.code}]` : "";
    return {
      message: `Request failed (HTTP ${e.status})${codeTag}${trailing}.`,
      code: env.code ?? env.reason,
      status: e.status,
    };
  }
  if (e instanceof Error) {
    return { message: e.message, code: null, status: null };
  }
  return { message: "Unknown error.", code: null, status: null };
}
